import React from 'react';
import Card from '../common/Card';
import { ServiceIcon } from './calendarHelpers';

interface KpiAnalysisTableProps {
  chartData: any[];
  selectedService: 'electricity' | 'water' | 'steam' | 'co2';
  chartViewMode: 'day' | 'week' | 'month';
  chartDayFocus: string;
  currentDate: Date;
  formatDateStr: (date: Date) => string;
  getServiceLabel: () => string;
}

export const KpiAnalysisTable: React.FC<KpiAnalysisTableProps> = ({
  chartData,
  selectedService,
  chartViewMode,
  chartDayFocus,
  currentDate,
  formatDateStr,
  getServiceLabel
}) => {
  const handleExportToExcel = () => {
    if (chartData.length === 0) return;

    const unit = selectedService === 'electricity' ? 'kWh' : selectedService === 'water' ? 'm³' : selectedService === 'steam' ? 'Tn' : 'Tn';
    const kpiUnit = selectedService === 'electricity' ? 'kWh/hl' : selectedService === 'water' ? 'm³/hl' : selectedService === 'steam' ? 'Tn/hl' : 'Tn/hl';

    // Headers
    const headers = [
      chartViewMode === 'day' ? 'Hora' : 'Dia/Fecha',
      'Volumen Planificado (hl)',
      'Volumen Real (hl)',
      `Consumo en Prediccion (${unit})`,
      `Consumo Real Medido (${unit})`,
      `KPI Predicho (${kpiUnit})`,
      `KPI Real (${kpiUnit})`,
      'Desviacion (%)',
      'Diagnostico'
    ];

    // Rows
    const rows = chartData.map(p => {
      const projVol = p['Prod. Proyectada (hl)'] || 0;
      const realVol = p['Prod. Real (hl)'] || 0;
      const predCons = p.predConsumo || 0;
      const realCons = p.realConsumo || 0;
      const cutoffHrs = p.corteHoras || 0;

      const kpiReal = realVol > 0 ? (realCons / realVol).toFixed(2) : '0.00';
      const kpiPred = projVol > 0 ? (predCons / projVol).toFixed(2) : '0.00';

      const devValue = predCons > 0 ? ((realCons - predCons) / predCons) * 100 : 0;
      const devStr = predCons > 0 ? `${devValue > 0 ? '+' : ''}${devValue.toFixed(1)}%` : '0.0%';

      let diagText = "Sin Operación";
      if (realCons > 0 || predCons > 0) {
        if (realCons > 0 && predCons === 0) {
          diagText = "Operación no planificada";
        } else if (Math.abs(devValue) <= 5) {
          diagText = "Precisión Óptima";
        } else if (devValue > 5) {
          diagText = cutoffHrs > 0 ? `Desviación por corte (${cutoffHrs}h)` : "Sobreconsumo / CIP extendido";
        } else {
          diagText = "Consumo menor al previsto";
        }
      }

      return [
        p.day,
        projVol,
        realVol,
        predCons,
        realCons,
        kpiPred,
        kpiReal,
        devStr.replace('%', ''),
        diagText
      ];
    });

    // Construir contenido CSV
    const csvContent = [
      headers.join(';'),
      ...rows.map(row => row.map(val => typeof val === 'string' ? `"${val}"` : val).join(';'))
    ].join('\n');

    // Crear y disparar descarga con UTF-8 BOM
    const blob = new Blob([new Uint8Array([0xEF, 0xBB, 0xBF]), csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    
    const formattedDate = chartViewMode === 'day' ? chartDayFocus : formatDateStr(currentDate);
    link.setAttribute('href', url);
    link.setAttribute('download', `kpi_analisis_${selectedService}_${chartViewMode}_${formattedDate}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (chartData.length === 0) return null;

  return (
    <Card className="border border-slate-350 shadow-sm overflow-hidden bg-white mt-6">
      <div className="p-4 border-b border-slate-200 bg-slate-50 flex flex-col md:flex-row md:justify-between md:items-center gap-3">
        <div>
          <h4 className="text-sm font-black text-slate-900 uppercase tracking-wider flex items-center gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="h-5 w-5 text-[#E2B53F]">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75ZM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V8.625ZM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V4.125Z" />
            </svg>
            <span>Análisis de KPI de Suministros y Desviaciones</span>
          </h4>
          <p className="text-xs text-slate-500 font-bold mt-0.5">
            KPI = Consumo / Volumen Real envasado (hl). Para la modulación de predicción, se divide consumo predicho entre volumen planificado.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleExportToExcel}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-300 hover:bg-slate-50 text-slate-800 font-extrabold text-[10px] uppercase tracking-wider rounded-lg shadow-sm transition-all"
            title="Exportar datos a Excel / CSV"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="h-3.5 w-3.5 text-emerald-600">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3" />
            </svg>
            Exportar a Excel
          </button>

          <div className="bg-zinc-950 text-white text-[10px] font-black uppercase tracking-widest px-3 py-1.5 rounded-lg shadow-sm flex items-center gap-1.5">
            <ServiceIcon type={selectedService} className="h-3.5 w-3.5 text-white" />
            <span>
              {selectedService === 'electricity' ? 'Energía' : selectedService === 'water' ? 'Agua' : selectedService === 'steam' ? 'Vapor' : 'CO2'}
            </span>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-left text-xs border-collapse">
          <thead>
            <tr className="bg-slate-100 text-slate-700 border-b border-slate-200">
              <th className="p-3 font-extrabold uppercase tracking-wider text-[10px]">{chartViewMode === 'day' ? 'Hora' : 'Día'}</th>
              <th className="p-3 font-extrabold uppercase tracking-wider text-[10px] text-right">Volumen Plan (hl)</th>
              <th className="p-3 font-extrabold uppercase tracking-wider text-[10px] text-right">Volumen Real (hl)</th>
              <th className="p-3 font-extrabold uppercase tracking-wider text-[10px] text-right">Consumo Predicción</th>
              <th className="p-3 font-extrabold uppercase tracking-wider text-[10px] text-right">Consumo Real</th>
              <th className="p-3 font-extrabold uppercase tracking-wider text-[10px] text-right">KPI Predicho</th>
              <th className="p-3 font-extrabold uppercase tracking-wider text-[10px] text-right">KPI Real</th>
              <th className="p-3 font-extrabold uppercase tracking-wider text-[10px] text-right">Desviación</th>
              <th className="p-3 font-extrabold uppercase tracking-wider text-[10px] text-center">Diagnóstico de Operación</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-150">
            {chartData.map((p, idx) => {
              const projVol = p['Prod. Proyectada (hl)'] || 0;
              const realVol = p['Prod. Real (hl)'] || 0;
              const predCons = p.predConsumo || 0;
              const realCons = p.realConsumo || 0;
              const cutoffHrs = p.corteHoras || 0;

              const unit = selectedService === 'electricity' ? 'kWh' : selectedService === 'water' ? 'm³' : selectedService === 'steam' ? 'Tn' : 'Tn';
              const kpiUnit = selectedService === 'electricity' ? 'kWh/hl' : selectedService === 'water' ? 'm³/hl' : selectedService === 'steam' ? 'Tn/hl' : 'Tn/hl';

              const kpiReal = realVol > 0 ? (realCons / realVol).toFixed(2) : '0.00';
              const kpiPred = projVol > 0 ? (predCons / projVol).toFixed(2) : '0.00';

              const devValue = predCons > 0 ? ((realCons - predCons) / predCons) * 100 : 0;
              const devStr = predCons > 0 ? `${devValue > 0 ? '+' : ''}${devValue.toFixed(1)}%` : '0.0%';

              // Diagnóstico
              let diagText = "Sin Operación";
              let diagBadge = "bg-slate-100 border-slate-200 text-slate-600";

              if (realCons > 0 || predCons > 0) {
                if (realCons > 0 && predCons === 0) {
                  diagText = "Operación no planificada";
                  diagBadge = "bg-amber-50 border-amber-200 text-amber-900";
                } else if (Math.abs(devValue) <= 5) {
                  diagText = "Precisión Óptima";
                  diagBadge = "bg-emerald-50 border-emerald-200 text-emerald-900";
                } else if (devValue > 5) {
                  if (cutoffHrs > 0) {
                    diagText = `Desviación por corte (${cutoffHrs}h)`;
                    diagBadge = "bg-rose-50 border-rose-200 text-rose-900 font-extrabold";
                  } else {
                    diagText = "Sobreconsumo / CIP extendido";
                    diagBadge = "bg-rose-50 border-rose-250 text-rose-900";
                  }
                } else {
                  diagText = "Consumo menor al previsto";
                  diagBadge = "bg-sky-50 border-sky-200 text-sky-900";
                }
              }

              return (
                <tr key={`kpi-row-${idx}`} className="hover:bg-slate-50/80 transition-colors">
                  <td className="p-3 font-bold text-slate-800">{p.day}</td>
                  <td className="p-3 text-right text-slate-600 font-semibold">{projVol.toLocaleString()} hl</td>
                  <td className="p-3 text-right text-slate-800 font-bold">{realVol.toLocaleString()} hl</td>
                  <td className="p-3 text-right text-slate-600 font-semibold">{predCons.toLocaleString()} {unit}</td>
                  <td className="p-3 text-right text-slate-800 font-bold">{realCons.toLocaleString()} {unit}</td>
                  <td className="p-3 text-right text-slate-500 font-semibold">{kpiPred} {kpiUnit}</td>
                  <td className="p-3 text-right text-slate-900 font-extrabold">{kpiReal} {kpiUnit}</td>
                  <td className={`p-3 text-right font-black ${
                    Math.abs(devValue) <= 5 ? 'text-emerald-700' : devValue > 5 ? 'text-red-600' : 'text-sky-700'
                  }`}>
                    {devStr}
                  </td>
                  <td className="p-3 text-center">
                    <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold border inline-block ${diagBadge}`}>
                      {diagText}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
};
